import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Subscription } from 'rxjs';
import { AppInfoService } from 'core/app-info/app-info.service';
import {
  countriesWithCreditCardFieldDependencies,
  countriesWithDoNotEmail,
  countriesWithProvince,
  countriesWithPaymentTermsDependencies,
  countryCodeValues,
  oemSkuField,
  hideDoNotEmail,
  countriesFromEurope,
  noAreaOrFinalDestination as countriesWithoutAreaOrFinalDestination,
} from 'core/app-info/country-specifics.enum';
import { UtilsService } from 'core/utils/utils.service';
import { AppStateService } from 'core/app-state/app-state.service';
import { NotificationType } from 'core/notification/notification-config.class';
import { NotificationService } from 'core/notification/notification.service';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { PaymentTemplateValues } from 'src/app/modules/shared/entities/payment-template-values.class';
import { PaymentAddress } from '../../../crm/contacts/entities/contact-address.class';
import { Customer } from '../../../shared/entities/customer.class';
import { Carrier } from '../entities/carrier.entity';
import { CustomerPaymentDetails } from '../entities/customer-payment-details.class';
import { PaymentDetails } from '../entities/payment-details.class';
import { PaymentSummary } from '../entities/payment-summary.class';
import { PaymentTerm } from '../entities/payment-term.class';

import { UploadAttachmentsEndpoints } from '../entities/upload-attachments-endpoints';
import { PaymentDetailsService } from '../services/payment-details.service';
import { QuoteDetailsService } from '../services/quote-details.service';
import { QuoteToolsService } from '../services/quote-tools.service';
import { SimpleUploadService } from '../services/simple-upload.service';

const attachmentsApiRoot = '/api/purchaseOrderAttachment/';
@Component({
  selector: 'app-payment-details',
  templateUrl: './payment-details.component.html',
  styleUrls: ['./payment-details.component.scss'],
  standalone: false
})
export class PaymentDetailsComponent implements OnInit, OnDestroy {
  @Output() goToQuoteOptions = new EventEmitter();
  appStrings: AppLocalization;
  isMobile: boolean;
  weightSymbol: string;
  shippingDetailsForm: FormGroup;
  billingDetailsForm: FormGroup;
  paymentDetailsForm: FormGroup;
  subs = new Subscription();
  routeSubs = new Subscription();
  emptyField = '-';
  maxPaymentTermsCount = 20;
  paymentTermsList: { value: number, label: string }[] = [];
  leadId: string;
  customerId: string;
  quoteId: string;
  carriersList: Carrier[] = [];
  displayCarriersList: { value: string, label: string }[] = [];
  billingAddressesList: PaymentAddress[] = [];
  displayBillingAddressesList: { value: string, label: string }[] = [];
  doNotMailBillingAddressesList: { value: string, label: string }[] = [];
  shippingAddress: PaymentAddress = new PaymentAddress();
  toSelectShippingAddress: PaymentAddress[] = []; // used when a lead quote needs shipping address manual selection
  billingAddress: PaymentAddress = new PaymentAddress();
  paymentSummary: PaymentSummary = new PaymentSummary();
  area: string = this.emptyField;
  countryList: { value: number, label: string }[] = [];
  lnCountryList: any[];
  currierChange = false;
  carrierMessage: string;
  paymentDetailsOutdated = false;
  paymentDetailsInProgress = false;
  quoteSaveInProgress = false;
  doNotEmailFormGroup: FormGroup;
  defaultAddresses: any[];
  canEdit: boolean;
  triggeredFieldValidations: boolean;
  customerDetails: CustomerPaymentDetails;
  testCustomer: any;
  customerPaymentDetails: CustomerPaymentDetails;
  hasTaxComputingError = false;
  isOnline: boolean;
  countriesToDisplayDoNotEmail;
  countryCode: string;
  shippingStateOrProvince;
  shipppingZipOrPostalCode;
  billingStateOrProvince;
  billingZipOrPostalCode;
  private panelClass = 'contacts-popUp';
  europeanCountry: boolean;
  hideAreaOrFinalDestination: boolean;
  zipNaming: any;
  countriesWithoutCC: boolean;
  displayOemSku: boolean;
  hideDoNotEmailField: boolean;
  noCreditCard: boolean;
  taxDetailsForm: FormGroup;
  poValidator = Validators.pattern('^[\x20-\x7E]*$'); // ASCII printable characters
  customerReferenceMaxLength = 30;
  fieldMaxLengths = {
    customerReference: 30,
    purchaseOrder: 30
  };

  @ViewChild('billingAddressInput') billingAddressInput: ElementRef;
  @Input() currencySymbol: string;
  taxNaming: string;
  taxRateNaming: string;
  customerCountryCode: string;
  isCreditCardEligible: boolean;
  taxForLead: string;
  originalCarriersList: any[];
  productCount: number;
  attachmentsCount: number;

  constructor(
    private appInfoService: AppInfoService,
    private quoteDetailsService: QuoteDetailsService,
    private paymentDetailsService: PaymentDetailsService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private simpleUploadService: SimpleUploadService,
    private notificationService: NotificationService,
    private appStateService: AppStateService,
    private utilsService: UtilsService,
    private deviceService: DeviceDetectorService,
    private cdRef: ChangeDetectorRef,
    public quoteToolService: QuoteToolsService
  ) {
  }


  ngOnInit() {
    this.isMobile = this.deviceService.isMobile();
    this.initData();
    this.routeSubs.add(this.route.paramMap.subscribe(() => {
      this.initDataForQuote();
    }));
  }

  private initData() {
    this.hideDoNotEmailField = hideDoNotEmail[this.customerCountryCode];
    this.appStrings = this.appInfoService.appStrings;
    this.weightSymbol = this.appInfoService.weightSymbol;
    this.isOnline = this.appStateService.state.isOnline;
    this.canEdit = !this.appStateService.checkIfElectronAppIsOutOfSync();
    this.taxDetailsForm = this.fb.group({
      freightInput: 0.00,
      taxRateInput: 0.00
    });
    this.countriesToDisplayDoNotEmail = countriesWithDoNotEmail;

    this.shippingDetailsForm = this.fb.group({
      customerReference: ['', Validators.maxLength(this.fieldMaxLengths.customerReference)],
      carrier: ['', Validators.required],
      finalDestination: ['', Validators.required],
      shipToComplete: false
    });

    this.billingDetailsForm = this.fb.group({
      billingAddressInput: '',
      doNotEmail: false,
    });
    this.doNotEmailFormGroup = this.billingDetailsForm.controls.doNotEmail as FormGroup;

    this.paymentDetailsForm = this.fb.group({
      id: [this.appInfoService.emptyGuid],
      paymentPurchaseOrder: ['', [Validators.required, this.poValidator, Validators.maxLength(this.fieldMaxLengths.purchaseOrder)]],
      paymentPurchaseOrderId: ['', Validators.required],
      creditCard: false,
      paymentPaymentTerms: ['', Validators.required],
      blockOrder: false
    });

    if (!this.canEdit) {
      this.setEnabledDisabledFormFields(this.canEdit);
    }
    this.paymentDetailsService.getStaticValues((templateValues: PaymentTemplateValues) => {
      const { CountryCode, LnCountry } = templateValues;
      this.updateCountryList(CountryCode);
      this.updateLNCountryList(LnCountry);
    });
    this.paymentDetailsService.resetCallsDoneFlag();
    // Regardless of the user (lead or customer), the layout is modified based on countryCode
    this.customerCountryCode = this.customerDetails?.lnCountryName;
    this.countryCode = this.customerCountryCode || this.appInfoService.userData.UserInfo.CountryCode;
    this.modifyLayoutBasedOnCountryCode(this.countryCode);

  }

  /**
   * Update country code
   */
  updateCustomerCountryCode() {
    if (this.customerId) {
      // For customers, we take in consideration the customer's country
      this.customerCountryCode = this.customerDetails?.lnCountryName;
    } else if (this.leadId) {
      // For customers, we take in consideration the company's country
      this.customerCountryCode = this.appInfoService.userData.UserInfo.CountryCode;
    }
    this.countryCode = this.customerCountryCode || this.appInfoService.userData.UserInfo.CountryCode;
    this.modifyLayoutBasedOnCountryCode(this.countryCode);
  }

  /**
   * Based on country, we decide what options are displayed on the payment details section
   * @param countryCode country code
   */
  modifyLayoutBasedOnCountryCode(countryCode: string) {
    this.setFieldsNaming(countryCode);
    this.displayOemSku = oemSkuField[countryCode];
    if (countriesFromEurope[countryCode]) {
      this.europeanCountry = true;
    } else {
      this.europeanCountry = false;
      this.determineStateOrProvince();
      this.displayOemSku = true;
    }

    if (countriesWithoutAreaOrFinalDestination[countryCode]) {
      this.hideAreaOrFinalDestination = true;
    } else {
      this.hideAreaOrFinalDestination = false;
    }
  }

  private initDataForQuote() {
    this.leadId = this.route.snapshot.paramMap.get('leadId');
    this.customerId = this.route.snapshot.paramMap.get('customerId');
    this.quoteId = this.route.snapshot.paramMap.get('quoteId');
    this.triggeredFieldValidations = false;
    this.subs.unsubscribe();
    this.subs = new Subscription();
    this.setSubscriptions();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    this.routeSubs.unsubscribe();
  }

  /**
   * Subscribes to customer set and payment id update
   */
  private setSubscriptions() {
    this.setPaymentDetailsSubscriptions();
    this.setQuoteDetailsSubscriptions();

    this.subs.add(this.quoteToolService.productCount$
      .subscribe(productsNumber => this.productCount = productsNumber));
  }

  /**
   * Setting payment details subscriptions
   */
  setPaymentDetailsSubscriptions() {
    this.paymentDetailsService.getDoNotMailAddresses().subscribe(value => {
      if (value.length) {
        this.defaultAddresses = value;
      } else {
        this.defaultAddresses = [new PaymentAddress(), new PaymentAddress()];
      }
      this.doNotMailBillingAddressesList = this.utilsService.mapObjectToSelect(this.defaultAddresses, 'id', 'name');
    });

    this.subs.add(this.paymentDetailsService.paymentSummary$.subscribe(paymentSummary => {
      this.paymentSummary = paymentSummary;
      if (this.countryCode === 'AU') {
        this.refreshCarriersListForAustralia();
      }

      if (this.paymentSummary.shouldRefreshCarriers) {
        this.refreshCarriersList();
      }

      this.weightSymbol = this.paymentSummary.weightUnit;

      if (this.paymentSummary.selectedCarrierCode) {
        this.mapCarriers(this.originalCarriersList, this.paymentSummary.selectedCarrierCode);
      }
      const freightCost = this.paymentSummary.freightCost || 0.00;
      const taxRate = this.paymentSummary.taxRate || 0.00;
      this.taxDetailsForm.controls.freightInput.setValue(freightCost);
      this.taxDetailsForm.controls.taxRateInput.setValue(taxRate);
    }));

    this.subs.add(this.paymentDetailsService.paymentSummaryError$.subscribe(isWithError => this.hasTaxComputingError = isWithError));
  }

  /**
   * Setting quote details subscriptions
   */
  setQuoteDetailsSubscriptions() {
    this.subs.add(this.quoteDetailsService.setCustomer$.subscribe((newCustomer: Customer) => {
      const oldAccountId = this.customerId || this.leadId;
      this.setNewCustomer(newCustomer);
      this.initializePaymentData(oldAccountId);
      this.testCustomer = newCustomer;
    }));

    this.subs.add(this.quoteDetailsService.refreshCarriers$.subscribe(() => {
      if (!this.carriersList?.length) {
        this.paymentDetailsService.getCarriersList(this.quoteId,
          carriers => this.mapCarriers(carriers, this.customerDetails?.carrierCode));
      }
    }));

    // for existing quote
    if (this.quoteDetailsService.isExistingQuote(this.quoteId)) {
      this.subs.add(this.quoteDetailsService.paymentId$.subscribe((paymentId: string) => {
        this.paymentDetailsService.getPaymentDetails(paymentId, paymentDetails =>
          this.paymentDetailsService.getPaymentTermsList(paymentTermsList => {
            this.paymentTermsList =
              this.utilsService.mapObjectToSelectMultipleLabel(paymentTermsList, 'id', ['termsOfPayment', 'description']);
            this.getCustomerSpecificListsForExistingQuote(paymentDetails);
            this.validatePurchaseOrderAttachments(paymentDetails.purchaseOrder?.id);
          }));
      }));
    }

    this.subs.add(this.quoteDetailsService.quoteIsSaving$.subscribe(value => {
      this.quoteSaveInProgress = value;
      this.setEnabledDisabledFormFields(!this.quoteSaveInProgress);
    }));

    this.subs.add(this.quoteDetailsService.isCompleted$.subscribe(isCompleted => {
      if (this.appStateService.checkIfElectronAppIsOutOfSync()) {
        return;
      }
      this.canEdit = !isCompleted;
      this.setEnabledDisabledFormFields(this.canEdit);
    }));

    this.subs.add(this.quoteDetailsService.creditCardEligibility$.subscribe(result => this.isCreditCardEligible = result));
  }

  refreshCarriersListForAustralia() {
    if ((this.paymentSummary.totalWeight || this.paymentSummary.totalWeight === 0) && this.productCount) {
      if (this.paymentSummary.totalWeight <= 5) {
        this.carriersList = this.originalCarriersList?.filter(item => !item.name.includes('COPE'));
      } else {
        this.carriersList = this.originalCarriersList?.filter(item => !item.name.includes('STARTRACK'));
      }
    }
    this.displayCarriersList = this.utilsService.mapObjectToSelect(this.carriersList, 'carrierCode', 'name');
  }

  private setEnabledDisabledFormFields(status) {
    if (this.appStateService.checkIfElectronAppIsOutOfSync()) {
      status = false;
    }
    this.paymentDetailsForm.controls.paymentPaymentTerms.disable();
    if (status) {
      this.shippingDetailsForm.controls.carrier.enable();
      this.shippingDetailsForm.controls.finalDestination.enable();
      this.shippingDetailsForm.controls.customerReference.enable();
      this.shippingDetailsForm.controls.shipToComplete.enable();
      this.paymentDetailsForm.controls.creditCard.enable();
      this.paymentDetailsForm.controls.blockOrder.enable();
      this.paymentDetailsForm.controls.paymentPurchaseOrder.enable();
      if (!this.paymentDetailsForm.controls.creditCard.value) {
        this.billingDetailsForm.controls.doNotEmail.enable();
      }
    } else {
      this.shippingDetailsForm.controls.carrier.disable();
      this.shippingDetailsForm.controls.finalDestination.disable();
      this.shippingDetailsForm.controls.customerReference.disable();
      this.shippingDetailsForm.controls.shipToComplete.disable();
      this.billingDetailsForm.controls.doNotEmail.disable();
      this.paymentDetailsForm.controls.creditCard.disable();
      this.paymentDetailsForm.controls.blockOrder.disable();
      this.paymentDetailsForm.controls.paymentPurchaseOrder.disable();
    }
  }

  carrierChanged(newVal: any) {
    const selectedCarrier = this.carriersList.find(carrier => carrier.carrierCode === newVal.value);
    this.quoteDetailsService.selectedCarrier$.next(selectedCarrier);
    this.markPaymentDetailsAsOutdated();
  }

  markPaymentDetailsAsOutdated() {
    this.paymentDetailsOutdated = true;
  }

  /**
   * Initializes payment section with data and lists
   * Runs when the customer is set (new quote, edit quote on load, change customer)
   * @param oldAccountId - The previously selected customer Id (if any) or lead id
   */
  private initializePaymentData(oldAccountId) {
    if (this.customerId) {
      if (!this.quoteId) {
        // if new quote was created and customer was selected for the first time
        this.paymentDetailsService.getPaymentTermsList(paymentTermsList => {
          this.paymentTermsList =
            this.utilsService.mapObjectToSelectMultipleLabel(paymentTermsList, 'id', ['termsOfPayment', 'description']);
          this.paymentDetailsService.getCustomerPaymentDetails(this.customerId, customerDetails => {
            this.customerDetails = customerDetails;
            this.updateCustomerCountryCode();
            this.getCustomerSpecificListsForNewQuote(customerDetails);
          });
        });
      } else if (oldAccountId) {
        if (oldAccountId !== this.customerId) {
          // if customer was changed by hand (not set at load, or for new quote)
          this.paymentDetailsService.getCustomerPaymentDetails(this.customerId, customerDetails => {
            this.customerDetails = customerDetails;
            this.updateCustomerCountryCode();
            // if customer was changed from a lead into customer
            if (!this.paymentTermsList.length) {
              this.paymentDetailsService.getPaymentTermsList(paymentTermsList => {
                this.paymentTermsList =
                  this.utilsService.mapObjectToSelectMultipleLabel(paymentTermsList, 'id', ['termsOfPayment', 'description']);
                this.getCustomerSpecificListsForChangedCustomer(customerDetails);
              });
            } else {
              this.getCustomerSpecificListsForChangedCustomer(customerDetails);
            }
          });
        } else {
          // the customer is the old one, shipping addresses and billing addresses already exists
          this.paymentDetailsService.callsDone.shippingAddresses = true;
          this.paymentDetailsService.callsDone.billingAddresses = true;
        }
      }
    }
    this.updateCustomerCountryCode();
  }

  displayContactName(contact: any) {
    return contact ? contact.name : '';
  }

  private mapObjectToKeys(objectFromServer: object) {
    return Object.keys(objectFromServer)
      .map(keyAsString => ({
        key: Number(keyAsString),
        value: objectFromServer[keyAsString]
      }));
  }

  /**
   * the default value for the final destination is the US. We can set it here as a default, and update it if it's an existing quote
   */
  private updateCountryList(countryListFromServer: object) {
    this.countryList = this.utilsService.mapNumberKeyValueOptions(countryListFromServer);
    const userCountryCode = this.appInfoService.userData.UserInfo.CountryCode;
    const countryId = this.countryList
      .find(country => country.label === countryCodeValues[userCountryCode])?.value || this.countryList[0].value;
    this.shippingDetailsForm.controls.finalDestination.setValue(countryId);
  }

  private updateLNCountryList(lnCountryListFromServer: object) {
    this.lnCountryList = this.mapObjectToKeys(lnCountryListFromServer);
  }

  /**
   * Gets default Carriers and maps Payment terms
   * Called only on new quote
   * @param customerDetails - The customer details
   */
  private getDefaulCarriersAndPaymentTerms(customerDetails: CustomerPaymentDetails) {
    if (!this.quoteId) {
      this.paymentDetailsService.getDefaultCarriersList(customerDetails.carrierCode, carriersList =>
        this.mapCarriers(carriersList, customerDetails.carrierCode), this.customerId);
    }
    this.mapPaymentTerm(this.paymentTermsList, customerDetails.paymentTermsId);
    this.setCreditCardFieldDependencies(customerDetails);
  }

  /**
   * Gets the customer specific details for a new quote
   * @param customerDetails - The customer details
   */
  private getCustomerSpecificListsForNewQuote(customerDetails: CustomerPaymentDetails) {
    this.getDefaulCarriersAndPaymentTerms(customerDetails);
    if (this.customerId) {
      this.checkIfCustomerHasShippingAddressId(customerDetails.shippingAddressId);
      this.paymentDetailsService.getCustomerBillingAddresses(this.customerId, billingAddresses =>
        this.mapBillingAddress(billingAddresses, customerDetails.billingAddressId, customerDetails.billingAddressTypeId));
    }
    this.area = customerDetails.area;
  }

  /**
   * Gets the customer specific details for an existing quote
   * @param paymentDetails - The payment details
   */
  private getCustomerSpecificListsForExistingQuote(paymentDetails: PaymentDetails) {
    this.paymentDetailsService.getPaymentSummary(this.quoteId);

    // customer specific calls - not for lead
    if (this.customerId) {
      this.getCustomerPaymentInfo(paymentDetails);
    }

    this.paymentDetailsForm.patchValue({
      id: paymentDetails.id,
      paymentPurchaseOrderId: paymentDetails.purchaseOrder ? paymentDetails.purchaseOrder.id : null,
      paymentPurchaseOrder: paymentDetails.purchaseOrder ? paymentDetails.purchaseOrder.purchaseOrderNumber : null,
      creditCard: paymentDetails.creditCard,
      blockOrder: paymentDetails.blockOrder
    });

    this.shippingDetailsForm.patchValue({
      shipToComplete: paymentDetails.shipToComplete
    });

    if (paymentDetails.quoteAttributes) {
      this.paymentDetailsService.saveQuoteAttributes(paymentDetails.quoteAttributes);
    }

    if (paymentDetails.contact) {
      this.paymentDetailsService.savedContact$.next(paymentDetails.contact);
    }

    this.setCreditCardFieldDependencies(paymentDetails);
  }

  /**
   * Getting customer payment info
   * @param paymentDetails payment details
   */
  getCustomerPaymentInfo(paymentDetails: PaymentDetails) {
    // Gets customer payment details - used for reverting Do Not Mail to customer default billing address
    this.paymentDetailsService.getCustomerPaymentDetails(this.customerId, customerDetails => {
      this.customerDetails = customerDetails;
      this.updateCustomerCountryCode();
      this.mapPaymentTerm(this.paymentTermsList, paymentDetails.paymentTermsId);
      let shippingId = paymentDetails.shippingAddressId;
      let carrierCode = Number(paymentDetails.carrierCode);
      let billingId = paymentDetails.billingAddressId;
      let billingAddressType = paymentDetails.billingAddressTypeId;

      if (!shippingId) {
        shippingId = customerDetails.shippingAddressId;
      }

      if (!carrierCode) {
        carrierCode = customerDetails.carrierCode;
      }

      if (!billingId) {
        billingId = customerDetails.billingAddressId;
        billingAddressType = customerDetails.billingAddressTypeId;
      }

      if (shippingId) {
        this.paymentDetailsService.getCustomerShippingAddress(shippingId,
          shippingAddress => this.mapShippingAddress(shippingAddress));
      } else {
        // no shippingId, allow the user to go to other quote section
        this.paymentDetailsService.callsDone.shippingAddresses = true;
      }

      this.paymentDetailsService.getCarriersList(this.quoteId,
        carriersList => this.mapCarriers(carriersList, carrierCode));
      this.paymentDetailsService.getCustomerBillingAddresses(this.customerId, billingAddresses =>
        this.mapBillingAddress(billingAddresses, billingId, billingAddressType));
    });

    this.area = paymentDetails.area;
    this.shippingDetailsForm.patchValue({
      customerReference: paymentDetails.customerReference,
      carrier: paymentDetails.carrierCode,
      finalDestination: paymentDetails.finalDestination
    });
    this.billingDetailsForm.patchValue({
      doNotEmail: paymentDetails.doNotMail,
    });
  }

  getCustomerSpecificListsForChangedCustomer(customerDetails: CustomerPaymentDetails) {
    this.mapPaymentTerm(this.paymentTermsList, customerDetails.paymentTermsId);
    this.paymentDetailsService.getCustomerBillingAddresses(this.customerId, billingAddresses =>
      this.mapBillingAddress(billingAddresses, customerDetails.billingAddressId, customerDetails.billingAddressTypeId));
    this.checkIfCustomerHasShippingAddressId(customerDetails.shippingAddressId);
    this.carriersList = [];
    this.displayCarriersList = [];
    this.area = customerDetails.area;
  }

  refreshCarriersList() {
    if (this.paymentDetailsForm.controls.id.value && this.paymentDetailsForm.controls.id.value !== this.appInfoService.emptyGuid) {
      this.markPaymentDetailsAsOutdated();
      this.paymentDetailsService.getPaymentDetails(this.paymentDetailsForm.controls.id.value, newPaymentDetails => {
        this.paymentDetailsService.getCarriersList(this.quoteId,
          carriersList => this.mapCarriers(carriersList, newPaymentDetails.carrierCode));
      });
    }
  }

  mapPaymentTerm(paymentTerms: any[], termId: number) {
    this.paymentTermsList = paymentTerms;
    let paymentTerm = this.paymentTermsList.find(pt => pt.value === termId);
    if (!paymentTerm) {
      paymentTerm = this.paymentTermsList.find(pt => pt.value === this.customerDetails.paymentTermsId);
    }
    this.paymentDetailsForm.controls.paymentPaymentTerms.setValue(paymentTerm?.value);
  }

  mapShippingAddress(shippingAddress: PaymentAddress) {
    this.shippingAddress = shippingAddress;
    if (!shippingAddress.addressline2 || !shippingAddress.addressline2.trim().length) {
      this.shippingAddress.addressline2 = '';
    }
    this.paymentDetailsService.callsDone.shippingAddresses = true;
  }

  mapBillingAddress(billingAddresses: PaymentAddress[], addressId: number, addressType) {
    const distinctBillingAddresses = [];

    // sort descending by address type id
    billingAddresses.sort((a, b) => b.billingAddressTypeId - a.billingAddressTypeId);

    // filter out billing addresses that are duplicated
    for (const item of billingAddresses) {
      const found = distinctBillingAddresses.find(address => item.name === address.name &&
        item.addressline1 === address.addressline1 &&
        item.addressline2 === address.addressline2 &&
        item.city === address.city &&
        item.state === address.state &&
        item.zip === address.zip);

      if (!found) {
        distinctBillingAddresses.push(item);
      }
    }

    this.billingAddressesList = distinctBillingAddresses;
    this.displayBillingAddressesList = this.utilsService.mapObjectToSelectWithDescription(
      this.billingAddressesList.map(address => ({ ...address, id: String(address.id) })),
      'id',
      'name',
      ['addressline1', 'addressline2', 'city', 'state', 'zip']
    );

    // if Do Not Mail is checked, don't set the mapped billing address
    if (!this.billingDetailsForm.controls.doNotEmail.value) {
      this.billingAddress = billingAddresses.find(addr =>
        addr.id === addressId && addr.billingAddressTypeId === addressType) || new PaymentAddress();

      setTimeout(() => {
        this.cdRef.detectChanges();
        this.billingDetailsForm.controls.billingAddressInput.setValue(this.billingAddress?.id ? String(this.billingAddress.id) : null);
      });
    }
    this.paymentDetailsService.callsDone.billingAddresses = true;
  }

  private mapCarriers(carriersList, carrierCode) {
    this.carriersList = carriersList;
    this.displayCarriersList = this.utilsService.mapObjectToSelect(this.carriersList, 'carrierCode', 'name');
    this.originalCarriersList = carriersList;
    if (this.countryCode === 'AU') {
      this.refreshCarriersListForAustralia();
    }

    if (!carrierCode || !carrierCode?.toString().trim()) {
      this.shippingDetailsForm.patchValue({ carrier: null });
      return;
    }

    const selectedCarrier = this.carriersList.find(carrier => carrier.carrierCode === carrierCode?.toString());

    this.shippingDetailsForm.patchValue({ carrier: selectedCarrier?.carrierCode });

    // notify that a carrier is selected, to correctly set default route
    this.quoteDetailsService.selectedCarrier$.next([null, selectedCarrier]);
  }

  private setNewCustomer(newCustomer: Customer) {
    if (newCustomer.isLead) {
      this.leadId = newCustomer.id;
      this.customerId = null;
    } else {
      this.customerId = newCustomer.id;
      this.leadId = null;
    }
  }

  /**
   * Tells if the form has changes
   */
  hasChanges() {
    return this.billingDetailsForm.dirty ||
      this.shippingDetailsForm.dirty ||
      this.paymentDetailsForm.dirty;
  }

  /**
   * Gets details needed for CC payment
   */
  getDetailsForCCPayment() {
    return {
      quoteId: this.quoteId,
      billingAddress: this.billingAddress
    };
  }

  /**
   * Returns payment details required by parent component
   */
  getPaymentDetails(creditCardPayment?, contact?, quoteAttributes?) {
    const pDetails = new PaymentDetails();
    pDetails.id = this.paymentDetailsForm.value.id;
    pDetails.quoteId = this.quoteId;
    pDetails.contact = contact || null;

    pDetails.area = this.shippingDetailsForm.value.area;
    pDetails.finalDestination = this.shippingDetailsForm.value.finalDestination;
    pDetails.doNotMail = this.doNotEmailFormGroup.value;
    pDetails.customerReference = this.shippingDetailsForm.value.customerReference;
    pDetails.creditCard = this.paymentDetailsForm.controls.creditCard.value;
    pDetails.blockOrder = this.paymentDetailsForm.controls.blockOrder.value;
    pDetails.purchaseOrderNumber = this.paymentDetailsForm.controls.paymentPurchaseOrder.value;
    pDetails.quoteAttributes = quoteAttributes || null;

    if (!creditCardPayment && !this.leadId) {
      pDetails.paymentTermsId = this.addPaymentTermsId();
    }

    pDetails.defaultAddressId = this.getDefaultAddressId();

    this.addCustomerBillingAndShipping(pDetails);

    return pDetails;
  }

  /**
   * Returns default address id
   */
  getDefaultAddressId() {
    if (this.doNotEmailFormGroup.value) {
      if (this.paymentDetailsForm.controls.creditCard.value) {
        return this.defaultAddresses[1].id;
      } else {
        return this.defaultAddresses[0].id;
      }
    } else {
      return null;
    }
  }

  /**
   * Returning payment terms id
   */
  addPaymentTermsId() {
    return this.paymentDetailsForm.controls.paymentPaymentTerms.value ?? 0;
  }

  /**
   * Adding billing and shipping info for a customer
   * @param pDetails payment details
   */
  addCustomerBillingAndShipping(pDetails: PaymentDetails) {
    if (this.customerId) {
      pDetails.shippingAddressId = this.shippingAddress.id;
      pDetails.billingAddressId = pDetails.defaultAddressId ? null : this.billingAddress.id;
      pDetails.billingAddressTypeId = this.billingAddress.billingAddressTypeId;
      pDetails.carrierCode = this.shippingDetailsForm.value.carrier?.toString() ?? null;
      pDetails.shipToComplete = this.shippingDetailsForm.value.shipToComplete;
    }
  }

  /**
   * Update Quote Number
   * @param quoteId - The quote id
   */
  updateQuoteId(quoteId) {
    this.quoteId = quoteId;
  }

  /**
   * Updates local payment details after save (id)
   * @param paymentDetails - Payment details
   */
  updatePaymentDetailsAfterSave(paymentDetails: PaymentDetails) {
    this.paymentDetailsForm.controls.id.setValue(paymentDetails.id);
    this.paymentDetailsForm.controls.paymentPurchaseOrderId.setValue(paymentDetails.purchaseOrder.id);
    this.billingDetailsForm.markAsPristine();
    this.shippingDetailsForm.markAsPristine();
    this.paymentDetailsForm.markAsPristine();
  }

  /**
   * Blurred Payment Terms
   */
  blurBillingAddress() {
    setTimeout(() => {
      const billingEl = this.billingAddressInput.nativeElement;
      if (billingEl.value && !this.billingAddress.id && this.billingAddress.name !== 'DO NOT MAIL') {
        this.billingAddress = new PaymentAddress();
        this.billingDetailsForm.patchValue({ billingAddressInput: null });
      }
    }, 100);
  }

  /**
   * Sets the billing address to the selected one
   * @param $event - The event
   */
  setBillingAddress($event: MatAutocompleteSelectedEvent) {
    const newBillingAddress = $event.option.value;
    this.billingDetailsForm.markAsDirty();
    if (newBillingAddress) {
      this.billingAddress = newBillingAddress;
    } else {
      this.billingAddress = new PaymentAddress();
    }
  }

  setBillingAddressMobile($event: MatAutocompleteSelectedEvent) {
    const selectedId = $event.option.value;
    this.billingAddress = this.billingAddressesList.find(addr => String(addr.id) === selectedId) || new PaymentAddress();
  }

  /**
   * Sets the shipping address to the selected one - used for merged quotes from leads
   * @param $event - The event
   */
  setShippingAddress($event: MatSelectChange) {
    this.shippingAddress = $event.value;
  }

  /**
   * Display function for payment terms
   * @param term - The payment term
   */
  displayPaymentTerm(term: PaymentTerm) {
    return term && term.termsOfPayment ? `${term.termsOfPayment} - ${term.description}` : this.emptyField;
  }

  /**
   * Display function for payment terms
   * @param address - The payment term
   */
  displayBillingAddress(address: PaymentAddress) {
    return address && address.name ? address.name : this.emptyField;
  }

  setDoNotMailFieldDependencies(paymentDetails: CustomerPaymentDetails) {
    if (this.billingDetailsForm.controls.doNotEmail.value) {
      if (this.paymentDetailsForm.controls.creditCard.value) {
        this.billingAddress = this.paymentDetailsService.getDoNotMailAddress(this.defaultAddresses[1]);
      } else {
        this.billingAddress = this.paymentDetailsService.getDoNotMailAddress(this.defaultAddresses[0]);
      }
      this.billingDetailsForm.controls.billingAddressInput.setValue(this.billingAddress?.id);
      this.billingDetailsForm.controls.billingAddressInput.disable();
      this.cdRef.detectChanges();
    } else {
      if (this.customerDetails && !paymentDetails) {
        this.mapBillingAddress(this.billingAddressesList, this.customerDetails.billingAddressId, this.customerDetails.billingAddressTypeId);
      }
      if (this.canEdit && !this.paymentDetailsForm.controls.creditCard.value) {
        this.billingDetailsForm.controls.billingAddressInput.enable();
      }
    }
  }

  setCreditCardFieldDependencies(paymentDetails?: CustomerPaymentDetails) {
    if (countriesWithPaymentTermsDependencies[this.countryCode]) {
      if (countriesWithCreditCardFieldDependencies[this.countryCode]) {
        this.checkCreditCardFieldDependencies();
      }

      if (this.paymentDetailsForm.controls.creditCard.value) {
        this.creditCardCheckedDependencies();
      } else {
        this.creditCardNotCheckedDependencies(paymentDetails);
      }

      if (countriesWithCreditCardFieldDependencies[this.countryCode]) {
        this.setDoNotMailFieldDependencies(paymentDetails);
      }
    }
  }
  /**
   * Setting dependencies when credit card is checked
   */
  creditCardCheckedDependencies() {
    this.paymentDetailsForm.controls.paymentPurchaseOrder
      .setValidators([this.poValidator, Validators.maxLength(this.fieldMaxLengths.purchaseOrder)]);
    // set payment term to P01 - ARC if credit card is used
    const arcPayment = this.paymentTermsList.find(pt => pt.label.includes('P01'));
    if (arcPayment) {
      this.paymentDetailsForm.controls.paymentPaymentTerms.setValue(arcPayment.value);
    }
    if (countriesWithCreditCardFieldDependencies[this.countryCode]) {
      // set Do Not Email to true
      this.billingDetailsForm.controls.doNotEmail.setValue(true);
    }
  }

  /**
   * Setting dependencies when credit card is not checked
   * @param paymentDetails payment details
   */
  creditCardNotCheckedDependencies(paymentDetails: CustomerPaymentDetails) {
    this.paymentDetailsForm.controls.paymentPurchaseOrder.setValidators([
      Validators.required,
      this.poValidator,
      Validators.maxLength(this.fieldMaxLengths.purchaseOrder)]);
    if (!paymentDetails) { // if credit card is checked from UI, ignore setValue if from API call response
      if (countriesWithCreditCardFieldDependencies[this.countryCode]) {
        this.billingDetailsForm.controls.doNotEmail.setValue(false);
      }
      this.mapPaymentTerm(this.paymentTermsList, this.customerDetails?.paymentTermsId);
    }
  }

  checkCreditCardFieldDependencies() {
    if (this.paymentDetailsForm.controls.creditCard.value) {
      this.billingDetailsForm.controls.doNotEmail.disable();
      this.billingDetailsForm.controls.billingAddressInput.disable();
    } else {
      if (this.canEdit && !this.billingDetailsForm.controls.doNotEmail.value) {
        this.billingDetailsForm.controls.billingAddressInput.enable();
      }
      if (this.canEdit) {
        this.billingDetailsForm.controls.doNotEmail.enable();
      }
    }
  }

  async openUploadAttachment(purchaseOrderId: string) {
    if (!purchaseOrderId) {
      return;
    }
    const endpoints = new UploadAttachmentsEndpoints();
    endpoints.listUrl = attachmentsApiRoot + 'list/';
    endpoints.downloadUrl = attachmentsApiRoot + 'get/';
    endpoints.uploadUrl = attachmentsApiRoot + 'upload';
    endpoints.deleteUrl = attachmentsApiRoot + 'delete';

    await this.simpleUploadService.openUploadDialog(endpoints, purchaseOrderId, 'PurchaseOrderAttachment', !this.canEdit);

    // Check if any purchase order file is attached, if so - remove the error
    this.validatePurchaseOrderAttachments(purchaseOrderId);
  }

  /**
   * Get the number of attached order files
   * @param as purchaseOrderId
   */
  validatePurchaseOrderAttachments(purchaseOrderId: string) {
    this.simpleUploadService.listFiles(attachmentsApiRoot + 'list/' + purchaseOrderId)
      .subscribe(attachments => this.attachmentsCount = attachments?.length);
  }

  /**
   * Marks form's controls as touched
   * @param as form {FormGroup} form
   */
  private markFormControlsAsTouched(form: FormGroup) {
    form.markAllAsTouched();
  }

  /**
   * Validate the Shipping Address information based on its country
   */
  private validateShippingAddress() {
    const paymentShippingDetailsForm = this.shippingDetailsForm;
    const fields = ['name', 'id', 'addressline1', 'city'];
    let isShippingValid = true;

    if (this.shippingAddress.country === 'USA' || this.shippingAddress.country === 'CAN') {
      fields.push('zip');
      if (!countriesFromEurope[this.countryCode]) {
        fields.push('state');
      }
    }

    for (const field of fields) {
      if (!Boolean(this.shippingAddress[field])) {
        isShippingValid = false;
      }
    }

    return paymentShippingDetailsForm.valid &&
      isShippingValid &&
      (!!this.shippingDetailsForm.controls.carrier.value || this.europeanCountry);
  }

  /**
   * Validate the Billing Address information based on its country
   */
  private validateBillingAddress() {
    const fields = ['name', 'addressline1', 'city'];
    let isBillingValid = true;

    if (this.billingAddress.country === 'USA' || this.billingAddress.country === 'CAN') {
      fields.push('zip');
      if (!countriesFromEurope[this.countryCode]) {
        fields.push('state');
      }
    }

    for (const field of fields) {
      if (!Boolean(this.billingAddress[field])) {
        isBillingValid = false;
      }
    }

    return isBillingValid;
  }

  /**
   * Validates component's forms
   */
  isPaymentValid() {
    const paymentDetailsForm = this.paymentDetailsForm;
    const paymentShippingDetailsForm = this.shippingDetailsForm;
    const paymentBillingDetailsForm = this.billingDetailsForm;
    if (this.europeanCountry) {
      paymentShippingDetailsForm.controls.carrier.setValidators(null);
      paymentShippingDetailsForm.controls.carrier.updateValueAndValidity();
    } else {
      paymentShippingDetailsForm.controls.carrier.setValidators([Validators.required]);
      paymentShippingDetailsForm.controls.carrier.updateValueAndValidity();
    }
    this.markFormControlsAsTouched(paymentDetailsForm);
    this.markFormControlsAsTouched(paymentShippingDetailsForm);
    this.markFormControlsAsTouched(paymentBillingDetailsForm);

    const isShippingValid = this.validateShippingAddress();
    const isBillingValid = this.validateBillingAddress();
    const isAttachmentValid = this.isPOAttachmentValid();

    const formsAreValid =
      paymentDetailsForm.valid &&
      isShippingValid &&
      isBillingValid && isAttachmentValid;

    if (!isShippingValid) {
      this.notificationService.showNotification(this.appStrings.shippingFormError, NotificationType.Error);
      return false;
    }

    if (!isBillingValid) {
      this.notificationService.showNotification(this.appStrings.billingFormError, NotificationType.Error);
      return false;
    }

    if (!formsAreValid) {
      this.notificationService.showNotification(this.appStrings.order_completeErrorsMessage, NotificationType.Error);
      return false;
    }

    return formsAreValid;
  }

  /**
   * Check if attachment is valid
   */
  isPOAttachmentValid() {
    // always valid when offline, since the po attachment field is not displayed in offline
    return Boolean(this.paymentDetailsForm.controls.creditCard.value) || Boolean(this.attachmentsCount) || !this.isOnline;
  }

  validatePayment() {
    if (this.paymentDetailsOutdated) {
      this.notificationService.showNotification(this.appStrings.quote_outdatedPaymentDetailsMessage, NotificationType.Warning);
      return false;
    }
    return true;
  }

  onContinueClick() {
    this.goToQuoteOptions.emit();
  }

  /**
   * Checks if the customer has shippingAddressId; If true, it gets the customer shipping address, otherwise
   * update the callsDone flag true on shippingAddresses
   * @param as shippingAddressId
   */
  private checkIfCustomerHasShippingAddressId(shippingAddressId: number) {
    if (shippingAddressId) {
      this.paymentDetailsService.getCustomerShippingAddress(shippingAddressId,
        shippingAddress => this.mapShippingAddress(shippingAddress));
    } else {
      this.paymentDetailsService.callsDone.shippingAddresses = true;
    }
  }

  determineStateOrProvince() {
    if (countriesWithProvince[this.countryCode]) {
      this.shippingStateOrProvince = this.appStrings.paymentDetails_shipping_province;
      this.shipppingZipOrPostalCode = this.appStrings.paymentDetails_shipping_postalCode;
      this.billingStateOrProvince = this.appStrings.paymentDetails_billing_province;
      this.billingZipOrPostalCode = this.appStrings.paymentDetails_billing_postalCode;
    } else {
      if (this.countryCode === 'AU') {
        this.shipppingZipOrPostalCode = this.appStrings.paymentDetails_shipping_postalCode;
        this.billingZipOrPostalCode = this.appStrings.paymentDetails_billing_postalCode;
      } else {
        this.shipppingZipOrPostalCode = this.appStrings.paymentDetails_shipping_zip;
        this.billingZipOrPostalCode = this.appStrings.paymentDetails_billing_zip;
      }
      this.shippingStateOrProvince = this.appStrings.paymentDetails_shipping_state;
      this.billingStateOrProvince = this.appStrings.paymentDetails_billing_state;
    }
  }

  // Determines whether the zip field should be named zip code or postal code, based on the country
  determineZipNaming() {
    if (this.countryCode === 'GB') {
      this.zipNaming = this.appStrings.paymentDetails_shipping_postalCode;
      this.shipppingZipOrPostalCode = this.appStrings.paymentDetails_european_postalCode;
      this.billingZipOrPostalCode = this.appStrings.paymentDetails_european_postalCode;
    } else if (this.countryCode === 'DE') {
      this.zipNaming = this.appStrings.paymentDetails_zipCode;
      this.shipppingZipOrPostalCode = this.appStrings.paymentDetails_european_zipCode;
      this.billingZipOrPostalCode = this.appStrings.paymentDetails_european_zipCode;
    }
  }

  /**
   * Sets the label name of the tax depending on selected country
   * @param countryCode country code
   */
  determineTaxNaming(countryCode: string) {
    if (countriesFromEurope[countryCode]) {
      this.taxNaming = this.appStrings.paymentDetails_european_summary_tax;
      this.taxRateNaming = this.appStrings.paymentDetails_european_summary_tax_rate;
    } else {
      if (countryCode === countryCodeValues.AU) {
        this.taxNaming = this.appStrings.paymentDetails_australia_summary_tax;
        this.taxRateNaming = this.appStrings.paymentDetails_australia_summary_tax_rate;
      } else {
        this.taxNaming = this.appStrings.paymentDetails_summary_tax;
        this.taxRateNaming = this.appStrings.paymentDetails_summary_tax_rate;
      }
    }
  }

  /**
   * Sets tax and zip field names based on selected country
   * @param countryCode country code
   */
  setFieldsNaming(countryCode: string) {
    this.determineZipNaming();
    this.determineTaxNaming(countryCode);
  }

  /**
   * Set save and payment button flag
   */

  setPaymentDetailsOutdated() {
    this.paymentDetailsOutdated =
      this.taxDetailsForm.controls.freightInput.value >= 0 || this.taxDetailsForm.controls.taxRateInput.value >= 0;
  }

  updateShipToComplete() {
    this.paymentDetailsService.shipToCompleteChecked$.next(this.shippingDetailsForm.controls.shipToComplete.value);
  }

  getAddressDisplay(address: any): string {
    return [
      address?.name ?? '-',
      address?.addressline1 ?? '-',
      address?.addressline2 ?? '-',
      address?.city ?? '-',
      address?.zip ?? '-',
      address?.state ?? '-'
    ].join(', ');
  }
}
